"use client";

import { ReactNode } from "react";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { HubExchangeAccount } from "@cede/types";
import { DISCORD_INVITE_URL, firstLetterToUpperCase } from "@cede/utils";
import { InformationModal } from "./InformationModal";
import { Button } from "@/components/ui/button";
import { useHubUser } from "@/hooks";
import { useOAuthSupportMap } from "@/hooks/exchangeAuthentication/useOAuthSupportMap";
import { useCreateAccountStore } from "@/store/accountStore";
import { FLOW_TYPES, STEPS, useConnectStore } from "@/store/connectStore";
import { useAutoRemoveExchangeAccountStore } from "@/store/useAutoRemoveExchangeAccountStore";

const Bold = ({ children }: { children: ReactNode }) => {
  return <span className="font-semibold text-cede-text-primary">{children}</span>;
};

type RemoveExchangeAccountModalContentProps = {
  onConnect: () => void;
  exchangeAccountToRemove: HubExchangeAccount | null;
  isLoading: boolean;
};

const RemoveExchangeAccountModalContent = ({
  onConnect,
  exchangeAccountToRemove,
  isLoading,
}: RemoveExchangeAccountModalContentProps) => {
  return (
    <div className="space-y-6 text-base text-cede-text-secondary">
      <div className="space-y-3 leading-relaxed">
        <p>
          For security reasons, please reconnect your{" "}
          <Bold>{firstLetterToUpperCase(exchangeAccountToRemove?.exchangeId || "")}</Bold> exchange account to continue
          to trade.
        </p>
        <p>Don’t worry your current positions in trading campaigns won't be affected.</p>
      </div>
      <div className="flex flex-col items-center gap-3 pt-2">
        <Button
          variant="primary"
          size="large"
          className="h-12 w-full rounded-2xl text-base font-semibold sm:w-[65%]"
          onClick={onConnect}
          loading={isLoading}
        >
          Connect
        </Button>
        <a
          href={DISCORD_INVITE_URL}
          target="_blank"
          rel="noreferrer"
          className="text-center text-sm font-medium text-cede-primary underline"
        >
          Need help? Let our team guide you!
        </a>
      </div>
    </div>
  );
};

interface RemoveExchangeAccountModalProps {
  open: boolean;
  onClose: () => void;
}

export const RemoveExchangeAccountModal = ({ open, onClose }: RemoveExchangeAccountModalProps) => {
  const { exchangeAccountToRemove } = useAutoRemoveExchangeAccountStore();
  const { setOpenConnectModal, setExchangeIds, setFlowType, setActiveStep } = useConnectStore();
  const { updateFormData } = useCreateAccountStore();
  const { removeExchangeAccountIsLoading } = useHubUser();
  const oAuthSupportMap = useOAuthSupportMap();

  const handleConnect = () => {
    const exchangeId = exchangeAccountToRemove?.exchangeId || "";
    setExchangeIds(Object.values(SUPPORTED_EXCHANGES));
    setFlowType(FLOW_TYPES.RELINK);
    const step = oAuthSupportMap[exchangeId] ? STEPS.LinkAccount : STEPS.ApiKeys;
    setActiveStep(step);
    setOpenConnectModal(true);
    updateFormData({ selectedCex: exchangeId });
    onClose();
  };

  return (
    <InformationModal open={open} onClose={onClose}>
      <RemoveExchangeAccountModalContent
        onConnect={handleConnect}
        exchangeAccountToRemove={exchangeAccountToRemove}
        isLoading={removeExchangeAccountIsLoading}
      />
    </InformationModal>
  );
};
